import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import QButton from './UI/QButton';
import { Skeleton } from '@material-ui/lab';

const QAppViz = ({
    qAppPromise,
    id,
    type,
    cols,
    options,
    noSelections,
    noInteraction,
    width,
    height,
    minWidth,
    minHeight,
    exportData,
    exportDataTitle,
    exportDataOptions,
    exportImg,
    exportImgTitle,
    exportImgOptions,
    exportPdf,
    exportPdfTitle,
    exportPdfOptions,
}) => {
    const [qViz, setQViz] = useState(null);
    const [error, setError] = useState(null);
    const node = useRef(null);
    const getExportButtonsData = () => {
        const exportButtons = [];
        if (exportData) {
            exportButtons.push({
                title: exportDataTitle,
                options: exportDataOptions,
                type: 'exportData',
            });
        }
        if (exportImg) {
            exportButtons.push({
                title: exportImgTitle,
                options: exportImgOptions,
                type: 'exportImg',
            });
        }
        if (exportPdf) {
            exportButtons.push({
                title: exportPdfTitle,
                options: exportPdfOptions,
                type: 'exportPdf',
            });
        }

        return exportButtons;
    };

    let qVizPromise = null;

    const btnStyle = {
        position: 'absolute',
        zIndex: 10,
        bottom: 0,
        right: 0,
    };

    const create = async () => {
        const qApp = await qAppPromise;
        if (qApp) {
            qVizPromise = id
                ? qApp.visualization.get(id)
                : qApp.visualization.create(type, cols, options);
            const _qViz = await qVizPromise;
            _qViz.setOptions(options);
            await setQViz(_qViz);
        }
    };

    const show = () => {
        qViz.show(node.current, { noSelections, noInteraction });
    };

    const close = () => {
        qViz.close();
    };

    const resize = () => {
        if (qViz) {
            qViz.resize();
        }
    };

    useEffect(() => {
        try {
            (async () => {
                if (!qViz) await create();
                if (qViz) show();
                window.addEventListener('resize', resize);
            })();
        } catch (_error) {
            setError(_error);
        }

        return () => {
            if (qViz) close();
            window.removeEventListener('resize', resize);
        };
        // eslint-disable-next-line
    }, [qViz]);

    const buildExportButton = (title, type, options) => (
        <div style={btnStyle} key={`${title}-${type}`}>
            <QButton type={type} qViz={qViz} title={title} options={options} />
        </div>
    );

    if (error) {
        return (
            <div style={{ height, width, minHeight, minWidth }}>
                {error.message}
            </div>
        );
    }

    if (!qViz) {
        return (
            <div style={{ width: '100%', height: '100%' }}>
                <Skeleton variant={'rect'} height={height} width={width} />
            </div>
        );
    }

    return (
        <div>
            <div ref={node} style={{ height, width, minHeight, minWidth }} />
            {getExportButtonsData().map(({ title, type, options }) =>
                buildExportButton(title, type, options)
            )}
        </div>
    );
};

QAppViz.propTypes = {
    qAppPromise: PropTypes.object.isRequired,
    id: PropTypes.string,
    type: PropTypes.oneOf([
        null,
        'barchart',
        'boxplot',
        'combochart',
        'distributionplot',
        'gauge',
        'histogram',
        'kpi',
        'linechart',
        'piechart',
        'pivot-table',
        'scatterplot',
        'table',
        'treemap',
        'extension',
    ]),
    cols: PropTypes.array,
    options: PropTypes.object,
    noSelections: PropTypes.bool,
    noInteraction: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exportData: PropTypes.bool,
    exportDataTitle: PropTypes.string,
    exportDataOptions: PropTypes.object,
    exportImg: PropTypes.bool,
    exportImgTitle: PropTypes.string,
    exportImgOptions: PropTypes.object,
    exportPdf: PropTypes.bool,
    exportPdfTitle: PropTypes.string,
    exportPdfOptions: PropTypes.object,
};

QAppViz.defaultProps = {
    id: null,
    type: null,
    cols: [],
    options: {},
    noSelections: false,
    noInteraction: false,
    width: '100%',
    height: '100%',
    minWidth: 'auto',
    minHeight: 'auto',
    exportData: false,
    exportDataTitle: 'Export Data',
    exportDataOptions: { format: 'CSV_T', state: 'P' },
    exportImg: false,
    exportImgTitle: 'Export Image',
    exportImgOptions: { width: 300, height: 400, format: 'JPG' },
    exportPdf: false,
    exportPdfTitle: 'Export Pdf',
    exportPdfOptions: {
        documentSize: 'a4',
        orientation: 'landscape',
        aspectRatio: 2,
    },
};

export default QAppViz;
